import React, { useState } from 'react';
import { CourtSurface } from '../../core/types';
import { X, Check, Activity, ShieldAlert, Sparkles, Sliders } from 'lucide-react';
import { tennisAudio } from '../../core/audio';

interface CourtCustomizerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSurface: CourtSurface;
  onSelectSurface: (s: CourtSurface) => void;
}

export const CourtCustomizerModal: React.FC<CourtCustomizerModalProps> = ({
  isOpen,
  onClose,
  currentSurface,
  onSelectSurface,
}) => {
  // String tension calculator state
  const [stringType, setStringType] = useState<'poly' | 'multi' | 'gut' | 'hybrid'>('poly');
  const [playerStyle, setPlayerStyle] = useState<'spin' | 'flat_power' | 'control' | 'arm_friendly'>('spin');
  const [headSize, setHeadSize] = useState<number>(98);
  const [weatherTemp, setWeatherTemp] = useState<'cold' | 'normal' | 'hot'>('normal');

  if (!isOpen) return null;

  const surfaces: Array<{
    id: CourtSurface;
    name: string;
    grandSlam: string;
    color: string;
    speed: string;
    bounce: string;
    description: string;
  }> = [
    {
      id: 'clay',
      name: '法网红土 (Roland Garros Red Clay)',
      grandSlam: '法国网球公开赛',
      color: '#c2410c',
      speed: '慢速 (Slow Pace)',
      bounce: '极高弹跳 (High Bounce)',
      description: '滑步击球，球落地后减速并带出强烈上旋高弹跳，极度考验体能与相持耐心。'
    },
    {
      id: 'grass',
      name: '温网草地 (Wimbledon Lawn Green)',
      grandSlam: '温布尔登锦标赛',
      color: '#15803d',
      speed: '极速 (Fast Pace)',
      bounce: '低平下窜 (Low Skid)',
      description: '百年传统天然草地，球落地后摩擦力极小向前低速滑窜，发球上网与切削最具杀伤力。'
    },
    {
      id: 'hardcourt_us',
      name: '美网经典蓝 (DecoTurf Midnight & Sky)',
      grandSlam: '美国网球公开赛',
      color: '#0284c7',
      speed: '中快速 (Medium-Fast)',
      bounce: '标准真弹跳 (True Bounce)',
      description: '硬地黄金标准，弹跳规律扎实，适合大力进攻型与全面型选手。'
    },
    {
      id: 'hardcourt_ao',
      name: '澳网极速蓝 (Pacific Blue)',
      grandSlam: '澳大利亚网球公开赛',
      color: '#06b6d4',
      speed: '中速 (Medium Pace)',
      bounce: '活跃高弹跳 (Lively Bounce)',
      description: '亚太大满贯蓝地球场，视觉对比度极高，白天炎热时球速飞快。'
    },
    {
      id: 'night_session',
      name: '碳纤夜场巡回赛 (Carbon Night Pro)',
      grandSlam: 'Joy Tennis 尊享特训场',
      color: '#334155',
      speed: '巡回赛速度 (Tour Pace)',
      bounce: '专业级弹跳 (Pro Bounce)',
      description: '极客暗黑荧光视觉，极具科技感与聚焦感，专为夜晚灯光赛打造。'
    }
  ];

  // Calculate recommended tension
  const calculateTension = () => {
    let base = 52; // Base tension in lbs
    if (stringType === 'poly') base -= 3; // Poly is stiffer
    if (stringType === 'gut') base += 2;
    if (playerStyle === 'control') base += 2;
    if (playerStyle === 'arm_friendly') base -= 4;
    if (headSize >= 100) base += 2;
    if (weatherTemp === 'cold') base -= 2;
    if (weatherTemp === 'hot') base += 2;

    const kg = (base * 0.453592).toFixed(1);
    return { lbs: base, kg };
  };

  const tensionResult = calculateTension();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/80 backdrop-blur-sm animate-fadeIn">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/80">
          <div className="flex items-center space-x-2">
            <Sliders className="w-5 h-5 text-lime-400" />
            <h2 className="text-base font-bold text-white">场地材质与球拍穿线定制工坊</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* 1. Surface Customizer */}
          <div>
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
              <span>🏟️ 欢乐俱乐部球场预设 (Court Surfaces)</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {surfaces.map((s) => {
                const isSelected = currentSurface === s.id;
                return (
                  <div
                    key={s.id}
                    onClick={() => {
                      onSelectSurface(s.id);
                      tennisAudio.playHit('topspin');
                    }}
                    className={`relative p-3 rounded-xl border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-lime-400 bg-slate-850 shadow-md ring-1 ring-lime-400/50'
                        : 'border-slate-800 bg-slate-900/60 hover:border-slate-700 hover:bg-slate-850/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center space-x-2">
                        <span className="w-3.5 h-3.5 rounded-full shadow" style={{ backgroundColor: s.color }}></span>
                        <span className="text-xs font-bold text-white">{s.name}</span>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-lime-400 shrink-0" />}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-400 mb-1">
                      <span className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-300 font-medium">{s.speed}</span>
                      <span className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-300 font-medium">{s.bounce}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-snug">{s.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <hr className="border-slate-800" />

          {/* 2. Racket String Tension Recommendation */}
          <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
            <h3 className="text-xs font-black uppercase tracking-wider text-amber-400 mb-3 flex items-center gap-1.5">
              <Activity className="w-4 h-4 text-amber-400" />
              <span>🎾 科学穿线磅数计算器 (String Tension Calculator)</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {/* String Type */}
              <div>
                <label className="block text-slate-400 mb-1 text-[11px]">拍线材质 (String Material)</label>
                <select
                  value={stringType}
                  onChange={(e) => setStringType(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-xs focus:border-lime-400"
                >
                  <option value="poly">聚酯硬线 (Polyester - 强旋转/控球)</option>
                  <option value="multi">多纤仿羊肠 (Multifilament - 舒适护臂)</option>
                  <option value="gut">天然羊肠线 (Natural Gut - 极佳手感)</option>
                  <option value="hybrid">子母线混合 (Hybrid Poly/Multi)</option>
                </select>
              </div>

              {/* Style */}
              <div>
                <label className="block text-slate-400 mb-1 text-[11px]">打球风格 (Playing Style)</label>
                <select
                  value={playerStyle}
                  onChange={(e) => setPlayerStyle(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-xs focus:border-lime-400"
                >
                  <option value="spin">强上旋暴抽型 (Heavy Topspin)</option>
                  <option value="flat_power">平击重炮型 (Flat Power)</option>
                  <option value="control">精准控制与落点型 (Control/Placement)</option>
                  <option value="arm_friendly">网球肘护腕柔和型 (Comfort/Arm Care)</option>
                </select>
              </div>

              {/* Head Size */}
              <div>
                <label className="block text-slate-400 mb-1 text-[11px]">球拍拍面大小 (Head Size)</label>
                <select
                  value={headSize}
                  onChange={(e) => setHeadSize(Number(e.target.value))}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-xs focus:border-lime-400"
                >
                  <option value={97}>97-98 sq.in (巡回赛小拍面/极致控制)</option>
                  <option value={100}>100 sq.in (黄金主流拍面/攻守兼备)</option>
                  <option value={104}>102-105 sq.in (大甜区/高容错)</option>
                </select>
              </div>

              {/* Weather */}
              <div>
                <label className="block text-slate-400 mb-1 text-[11px]">气温环境 (Ambient Temperature)</label>
                <select
                  value={weatherTemp}
                  onChange={(e) => setWeatherTemp(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-xs focus:border-lime-400"
                >
                  <option value="normal">常温 20°C - 26°C (标准环境)</option>
                  <option value="cold">寒冷 &lt;15°C (气温低易变硬，需降磅)</option>
                  <option value="hot">炎热 &gt;30°C (气温高弹力增大，需加磅)</option>
                </select>
              </div>
            </div>

            {/* Tension Result Card */}
            <div className="mt-4 p-3 bg-gradient-to-r from-slate-900 to-slate-850 rounded-xl border border-lime-500/30 flex items-center justify-between">
              <div>
                <div className="text-[10px] text-lime-400 font-bold uppercase tracking-wider">
                  AI推荐最佳拉线磅数
                </div>
                <div className="text-xl font-black text-white flex items-baseline gap-1.5 mt-0.5">
                  <span className="text-lime-400">{tensionResult.lbs} 磅 (lbs)</span>
                  <span className="text-xs text-slate-400 font-normal">/ {tensionResult.kg} 公斤 (kg)</span>
                </div>
              </div>
              <div className="text-[11px] text-slate-400 text-right max-w-[200px] leading-tight">
                建议竖线与横线使用相同磅数，穿硬线20-30小时后自然掉磅5-8磅属正常现象。
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800 bg-slate-950 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl bg-lime-500 hover:bg-lime-400 text-slate-950 text-xs font-black transition-all shadow-md"
          >
            完成并应用
          </button>
        </div>
      </div>
    </div>
  );
};
